import { useState, useCallback, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { ExpandMore, ExpandLess, Save, MoreVert } from '@material-ui/icons'
import styled from 'styled-components'

import { TOAST_TYPE } from '@base'
import { Button, Input, Dropdown as DropdownV2 } from '@baseV2'
import { addTrip, updateInvoiceTracker, closeTrip, vehicleKycApi } from '@data'
import { showToast, showLoader } from '@data/state/action'
import { toggleModal, updateDrawerMeta } from '@data/state/action/root'
import { DELIVERY_STATUS, MODAL_TYPES, strings } from '@constants'
import { Spacings, Text } from '@styles'

import BasicModal from '@base/basic/BasicModal'
import FrieghttigerTrip from '../../FrieghttigerTrip'
import { saveInvoice, buildBaseInvoicePayload } from './invoiceApi'

import {
	BasicDetailsCard,
	CardHeader,
	CardTitle,
	ChevronBtn,
	DetailGroup,
	DetailRow,
	DetailLabel,
	DetailValue,
	VehicleHeaderTitleRow,
	StatusChip,
	VehicleInputSection,
	VehicleInputGroup,
	VehicleInputLabel,
	VehicleActionButtons,
	IconActionBtn,
} from './style'

const ModalWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_2B};
`

const ModalTitle = styled.h5`
	font-size: ${Text.LARGE};
	font-weight: ${Text.BOLD_600};
	color: ${({ theme }) => theme.palette.text.darkblack};
	padding-bottom: ${Spacings.SPACING_3B};
	border-bottom: 1px solid ${({ theme }) => theme.palette.background.greyBorder};
	margin: 0 0 ${Spacings.SPACING_2B};
`

const ModalActions = styled.div`
	display: flex;
	gap: ${Spacings.SPACING_2B};
	margin-top: 3rem;
	justify-content: flex-end;
`

const TripBtn = styled(Button).attrs({ variant: 'outline', color: 'primary' })`
	white-space: nowrap;
	padding-top: ${Spacings.SPACING_2B};
	padding-bottom: ${Spacings.SPACING_2B};
	font-size: ${Text.MEDIUM};
`

const DRIVER_REGEX = /^[0]?[6789]\d{9}$/
const VEHICLE_REGEX = /^[A-Z]{2}[ -]?[0-9]{1,2}[ -]?[A-Z]{1,3}[ -]?[0-9]{4}$/

const DELIVERY_STATUS_CHIP = {
	[DELIVERY_STATUS.LOADED]: { label: strings('loaded'), variant: 'loaded' },
	[DELIVERY_STATUS['EN ROUTE']]: { label: 'EN ROUTE', variant: 'transit' },
	[DELIVERY_STATUS.DELIVERED]: { label: strings('delivered'), variant: 'delivered' },
	[DELIVERY_STATUS.SALES_RETURN]: { label: 'SALES RETURN', variant: 'expired' },
}

const normalizeTripState = (ts) => String(ts ?? '').trim().replace(/-/g, '_').toUpperCase()

const TRIP_STATE_CHIP = {
	BEFORE_ORIGIN: { label: 'BEFORE ORIGIN', variant: 'loaded' },
	AT_ORIGIN: { label: 'AT PICKUP', variant: 'loaded' },
	IN_TRANSIT: { label: 'EN ROUTE', variant: 'transit' },
	AT_DESTINATION: { label: 'AT DESTINATION', variant: 'delivered' },
}

// Latest trip_state → chip { label, variant }. Unmapped states fall back to a readable label.
const tripStateToChip = (tripState) => {
	if (!tripState) return null
	const key = normalizeTripState(tripState)
	return TRIP_STATE_CHIP[key] ?? { label: key.replace(/_/g, ' '), variant: 'transit' }
}

const VehicleDetails = ({ invoiceData, order, supplierPoNumber, update, editable = false }) => {
	const dispatch = useDispatch()
	const user = useSelector((state) => state.getIn(['user', 'user']))

	const [isOpen, setIsOpen] = useState(true)
	const [driverNumber, setDriverNumber] = useState('')
	const [vehicleNumber, setVehicleNumber] = useState('')
	const [isTripOpen, setIsTripOpen] = useState(false)
	const [isSaving, setIsSaving] = useState(false)

	useEffect(() => {
		setDriverNumber(invoiceData?.driver_number || '')
		setVehicleNumber(invoiceData?.vehicle_number || '')
	}, [invoiceData?.driver_number, invoiceData?.vehicle_number])

	const hasTrip = !!invoiceData?.trip_id
	const hasChanges = driverNumber !== (invoiceData?.driver_number || '') || vehicleNumber !== (invoiceData?.vehicle_number || '')
	// Status = trip_state of the LAST object in the invoice's vehicle_status_list; fall back to the
	// delivery_status chip when there is no vehicle status yet.
	const vehicleStatusList = Array.isArray(invoiceData?.vehicle_status_list) ? invoiceData.vehicle_status_list : []
	const lastTripState = vehicleStatusList.length ? vehicleStatusList[vehicleStatusList.length - 1]?.trip_state : null
	const deliveryStatusChip = tripStateToChip(lastTripState) ?? DELIVERY_STATUS_CHIP[invoiceData?.delivery_status]
	
	const driverError = driverNumber && !DRIVER_REGEX.test(driverNumber) ? strings('enter_valid_num') : null
	const vehicleError = vehicleNumber && !VEHICLE_REGEX.test(vehicleNumber) ? strings('pls_enter_valid_vehicle_num') : null

	const isAddTripDisabled = useMemo(() => {
		if (!vehicleNumber.trim()) return true
		if (vehicleNumber && !VEHICLE_REGEX.test(vehicleNumber)) return true
		if (driverNumber && !DRIVER_REGEX.test(driverNumber)) return true
		return false
	}, [vehicleNumber, driverNumber])

	const saveDriverVehicle = useCallback(async () => {
		setIsSaving(true)
		const payload = {
			...invoiceData,
			...buildBaseInvoicePayload(invoiceData, order, supplierPoNumber),
			driver_number: driverNumber,
			vehicle_number: vehicleNumber,
			updated_by: user?.user_id,
		}
		const ok = await saveInvoice({
			payload,
			fallbackInvoice: { ...invoiceData, driver_number: driverNumber, vehicle_number: vehicleNumber },
			order,
			invoiceData,
			dispatch,
			supplierPoNumber,
			update,
		})
		setIsSaving(false)
		return ok
	}, [driverNumber, vehicleNumber, invoiceData, order, user, dispatch, supplierPoNumber, update])

	const handleSave = useCallback(() => {
		saveDriverVehicle()
	}, [saveDriverVehicle])

	const handleAddTrip = useCallback(async () => {
		if (hasChanges) {
			const ok = await saveDriverVehicle()
			if (!ok) return
		}
		setIsTripOpen(true)
	}, [hasChanges, saveDriverVehicle])

	const handleEditTrip = useCallback(() => {
		setIsTripOpen(true)
	}, [])

	const handleConfirmTrip = useCallback(async () => {
		dispatch(showLoader(true))
		try {
			const api = invoiceData?.trip_id
				? updateInvoiceTracker(driverNumber, invoiceData.invoice_number, invoiceData.trip_id, order.order_id, vehicleNumber)
				: addTrip(order.order_id, invoiceData.invoice_number)
			const response = await api
			dispatch(
				showToast(true, invoiceData?.trip_id ? strings('update_trip_successfully') : strings('add_trip_success'), {
					type: TOAST_TYPE.SUCCESS,
				})
			)
			dispatch(updateDrawerMeta({ invoiceData: { ...response } }))
			setIsTripOpen(false)
		} catch (err) {
			dispatch(showToast(true, err?.response?.data?.message, { type: TOAST_TYPE.ERROR }))
		} finally {
			dispatch(showLoader(false))
		}
	}, [driverNumber, vehicleNumber, invoiceData, order])

	const handleCloseTrip = useCallback(() => {
		dispatch(
			toggleModal(true, MODAL_TYPES.CONFIRMATION, {
				overlayClickable: true,
				title: 'Are you sure you want to close this trip?',
				confirmAction: (actionType) => {
					if (actionType) {
						dispatch(showLoader(true))
						closeTrip(order.order_id, invoiceData.invoice_number)
							.then((response) => {
								// Backend returns [{ status, response }] — a 200 can still carry a business
								// failure (e.g. "Trip is already closed"). Show that message instead of a
								// generic success, and don't overwrite invoiceData with the error payload.
								const result = Array.isArray(response) ? response[0] : response
								if (result?.status === false) {
									dispatch(showToast(true, result?.response, { type: TOAST_TYPE.ERROR }))
									return
								}
								dispatch(showToast(true, strings('stop_trip_successfully'), { type: TOAST_TYPE.SUCCESS }))
								dispatch(updateDrawerMeta({ invoiceData: { ...response } }))
							})
							.catch((err) => {
								// The 422 message looks like: `422 Unprocessable Content: [{"status":false,"response":"Trip is already closed"}]`.
								// Pull the embedded JSON and surface only its `response` text.
								const raw = err?.response?.data?.message
								let msg = raw
								const jsonPart = typeof raw === 'string' && raw.match(/\[.*\]|\{.*\}/)
								if (jsonPart) {
									try {
										const parsed = JSON.parse(jsonPart[0])
										const item = Array.isArray(parsed) ? parsed[0] : parsed
										if (item?.response) msg = item.response
									} catch (e) {
										// keep the raw message
									}
								}
								dispatch(showToast(true, msg, { type: TOAST_TYPE.ERROR }))
							})
							.finally(() => dispatch(showLoader(false)))
					}
					dispatch(toggleModal(false))
				},
			})
		)
	}, [order, invoiceData])

	const handleFetchVehicleDetails = useCallback(() => {
		dispatch(showLoader(true))
		vehicleKycApi(order.order_id, invoiceData.invoice_number)
			.then((response) => {
				dispatch(showToast(true, strings('msg_ehicle_kyc_fetch_success'), { type: TOAST_TYPE.SUCCESS }))
				dispatch(updateDrawerMeta({ invoiceData: { ...invoiceData, vehicle_details: response } }))
			})
			.catch((error) => {
				dispatch(showToast(true, error?.response?.data?.message || strings('msg_vehicle_kyc_fetch_failure'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => dispatch(showLoader(false)))
	}, [order, invoiceData])

	const vehicleDetailRows = useMemo(() => {
		if (!invoiceData?.vehicle_details) return []
		const d = invoiceData.vehicle_details
		return [
			{ label: strings('vehicle_category'), value: d.vehicle_category },
			{ label: strings('vehicle_class_description'), value: d.vehicle_class_description },
			{ label: strings('maker_model'), value: d.maker_model },
			{ label: strings('body_type_description'), value: d.body_type_description },
			{ label: strings('cubic_capacity'), value: d.cubic_capacity },
		].filter(({ value }) => value)
	}, [invoiceData?.vehicle_details])

	return (
		<BasicDetailsCard>
			<CardHeader>
				<VehicleHeaderTitleRow>
					<CardTitle>{strings('vehicle', 'details')}</CardTitle>
					{deliveryStatusChip && <StatusChip $variant={deliveryStatusChip.variant}>{deliveryStatusChip.label}</StatusChip>}
				</VehicleHeaderTitleRow>
				<ChevronBtn onClick={() => setIsOpen((v) => !v)}>{isOpen ? <ExpandLess /> : <ExpandMore />}</ChevronBtn>
			</CardHeader>

			{isOpen && (
				<>
					<VehicleInputSection>
						<VehicleInputGroup>
							<VehicleInputLabel>{strings('driver', 'number')}</VehicleInputLabel>
							<Input
								type='text'
								small
								value={driverNumber}
								onChange={(e) => setDriverNumber(e.target.value)}
								disabled={isSaving || !editable}
								error={driverError}
							/>
						</VehicleInputGroup>
						<VehicleInputGroup>
							<VehicleInputLabel>{strings('vehicle', 'number')}</VehicleInputLabel>
							<Input
								type='text'
								small
								value={vehicleNumber}
								onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
								disabled={isSaving || !editable}
								error={vehicleError}
							/>
						</VehicleInputGroup>
						<VehicleActionButtons>
							<TripBtn disabled={!editable || isSaving || !hasChanges || !!driverError || !!vehicleError} onClick={handleSave}>
								{strings('save')}
							</TripBtn>
							{!supplierPoNumber && (
								<TripBtn disabled={!editable || isSaving || isAddTripDisabled || hasChanges} onClick={hasTrip ? handleEditTrip : handleAddTrip}>
									{hasTrip ? strings('edit', 'trip') : strings('add', 'trip')}
								</TripBtn>
							)}
							{hasTrip && (
								<DropdownV2
									trigger={({ onClick }) => (
										<IconActionBtn onClick={onClick}>
											<MoreVert fontSize='small' />
										</IconActionBtn>
									)}
									options={[
										{ label: 'Close Trip', color: 'danger', onClick: handleCloseTrip },
										{
											label: strings('vehicle', 'details'),
											onClick: handleFetchVehicleDetails,
											hide: !invoiceData?.vehicle_number,
										},
									]}
								/>
							)}
						</VehicleActionButtons>
					</VehicleInputSection>

					{vehicleDetailRows.length > 0 && (
						<DetailGroup>
							{vehicleDetailRows.map(({ label, value }) => (
								<DetailRow key={label}>
									<DetailLabel>{label}</DetailLabel>
									<DetailValue>{value}</DetailValue>
								</DetailRow>
							))}
						</DetailGroup>
					)}
				</>
			)}

			<BasicModal width='40vw' isOpen={isTripOpen} onClose={() => setIsTripOpen(false)} closeIcon={true}>
				<ModalWrapper>
					<ModalTitle>{hasTrip ? strings('update', 'trip') : strings('add', 'trip')}</ModalTitle>
					<FrieghttigerTrip invoiceData={invoiceData} order={order} setOpen={setIsTripOpen} update={() => {}} />
					<ModalActions>
						<Button xs type='secondary' variant='destructive' onClick={() => setIsTripOpen(false)}>
							{strings('cancel')}
						</Button>
						<Button xs type='primary' onClick={handleConfirmTrip}>
							{strings('confirm')}
						</Button>
					</ModalActions>
				</ModalWrapper>
			</BasicModal>
		</BasicDetailsCard>
	)
}

export default VehicleDetails
